<template>
  <div class="action_kme_container">
    <div class="action_kme_box">
      <!-- 1. 映射关系看板 (Source -> Target) -->
      <div class="kme_mapping_board">
        <div class="board_inner">
          <!-- 当前按键 (只读) -->
          <div class="key_slot source">
            <div class="slot_label">{{ t('current_selected_key') }}</div>
            <div class="key_display active">{{ currentKey }}</div>
          </div>

          <!-- 连动装饰 -->
          <div class="mapping_flow">
            <div class="flow_line"></div>
            <div class="flow_icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <div class="flow_label">SOCD</div>
          </div>

          <!-- 目标按键 (可点击更改) -->
          <trigger_btn class="key_slot target" :label="t('target_key')" :vks="Keys[0]?.KeyCode || []" :tab="tab" :show-edit="true" :large="true" @trigger-click="showKeyDialog = true" />
        </div>
      </div>

      <!-- 2. 模式选项列表 -->
      <div class="modes_section" v-if="isKMEModeSupported">
        <div class="modes_group">
          <!-- 循环渲染模式 (为了代码复用) -->
          <div v-for="opt in modeConfig" :key="opt.type" class="mode_card_item" :title="opt.desc" :class="{ active: mode == opt.type }" @click="changeMode(opt.type)">
            <div class="mode_main_content">
              <div class="mode_icon_box" :class="opt.class">
                <div class="dot_indicator"></div>
              </div>
              <div class="mode_info_text">
                <div class="mode_name">{{ opt.title }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. 特殊固件延迟设置 -->
      <div class="kme_settings_section" v-if="specialCheckCurrent(speciaBehiver.DevicesOfJingtai_V2)">
        <div class="mgx_setting_item_card">
          <div class="setting_header">
            <div class="header_left">
              <svg class="pill_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" stroke-width="2" />
                <path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round" />
              </svg>
              <span class="pill_title">{{ $t('action_set_delay_time') }}</span>
            </div>
            <span class="pill_value">{{ delayTime }}<span>ms</span></span>
          </div>
          <div class="setting_slider_container">
            <input v-model.number="delayTime" class="slider_input" type="range" max="50" min="0" step="1" :style="{ backgroundSize: timeBackgroundSize }" />
            <div class="slider_labels">
              <span>0ms</span>
              <span>50ms</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <action_key_dialog v-model:visible="showKeyDialog" :row-index="0" :base-tab="tab" :selected-keys="Keys[0]?.KeyCode || [0, 0, 0]" @confirm="onKeyConfirm" />
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import i18n from '../../languages/i18n'
import { actionSet } from '@/service/driver/action_set'
import { SetType, BaseTabs } from '@/service/driver/action_types'
import { kks, VK } from '@/define/define'
import { toRefs } from 'vue'
import { MagnetSeniorType, SeniorKMEType } from '@libs/types/enum'
import { onMounted } from 'vue'
import { watch } from 'vue'
import { operate } from '@/service/driver/operate'
import { computed } from 'vue'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { onUnmounted } from 'vue'
import { DeviceCapability } from '@/service/device_capability'
import { normalizeKeyCode } from '@/tool/utility'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { currentSelectedKeyText, getVkText } from './action_config'

const { t } = i18n.global
const mode = ref(SeniorKMEType.Default)
const isKMEModeSupported = ref(false)
const showKeyDialog = ref(false)
const delayTime = ref(0)
const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (Keys.value.length === 0) {
    Keys.value = [
      {
        KeyCode: keys,
        KeyFunction: [],
        Travel: 0,
      },
    ]
  } else {
    Keys.value[0].KeyCode = keys
  }
}

const changeMode = (value: SeniorKMEType) => {
  mode.value = value
  const modeData = modeList.get(value)
  if (modeData != undefined) {
    KMEMode.value = modeData
  }
}

const modeList = new Map<SeniorKMEType, [number, number]>([
  [SeniorKMEType.Default, [0, 0]],
  [SeniorKMEType.AFirst, [0, 1]],
  [SeniorKMEType.BFirst, [1, 0]],
  [SeniorKMEType.Neutral, [1, 1]],
])

const props = defineProps<{ tab: BaseTabs; keys: VK[]; type: SetType }>()
const emit = defineEmits(['changeSelectedKeys'])

let { Keys, KeyType, KMEMode, DelayTime } = toRefs(actionSet.magnetValue)

const initData = () => {
  if (Keys.value.length != 1) {
    Keys.value = [
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [],
        Travel: 0,
      },
    ]
  }
  KeyType.value = MagnetSeniorType.SeniorKeyTypeKME
  for (const [t, modeData] of modeList) {
    if (modeData[0] == KMEMode.value[0] && modeData[1] == KMEMode.value[1]) {
      mode.value = t
    }
  }
  delayTime.value = actionSet.modeTimes[SetType.kme]
}

onMounted(async () => {
  isKMEModeSupported.value = await DeviceCapability.isKMEModeSupported()
  initData()
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)

watch(delayTime, (val) => {
  const v = Number(val)
  actionSet.modeTimes[SetType.kme] = v
  DelayTime.value = v
})

onUnmounted(() => {
  isKMEModeSupported.value = false
})

const currentKey = computed(() => currentSelectedKeyText.value)

const selectedKey = computed(() => {
  const code = Keys.value[0]?.KeyCode?.[2] || 0
  return getVkText(code)
})

const modeConfig = computed(() => [
  {
    type: SeniorKMEType.Default,
    title: t('kme_mode_1'),
    desc: t('kme_mode_1_desc'),
    class: 'last_input',
  },
  {
    type: SeniorKMEType.AFirst,
    title: t('kme_mode_2', { key: currentKey.value || t('kme_key_unspecified') }),
    desc: t('kme_mode_2_desc', { key: currentKey.value || t('kme_key_unspecified') }),
    class: 'a_first',
  },
  {
    type: SeniorKMEType.BFirst,
    title: t('kme_mode_2', { key: selectedKey.value || t('kme_key_unspecified') }),
    desc: t('kme_mode_2_desc', { key: selectedKey.value || t('kme_key_unspecified') }),
    class: 'b_first',
  },
  {
    type: SeniorKMEType.Neutral,
    title: t('kme_mode_4'),
    desc: t('kme_mode_4_desc'),
    class: 'neutral',
  },
])

const timeBackgroundSize = computed(() => {
  const timePercent = (delayTime.value / 50) * 100
  return `${timePercent}% 100%`
})

const closeDelayTime = () => {
  // V2 目前是行内滑块，没有弹窗状态，保留空函数以兼容调用
}

defineExpose({ closeDelayTime })
</script>

<style lang="less" scoped>
.action_kme_container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
}

.action_kme_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;

  /* 映射关系看板 - 紧凑化 */
  .kme_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-m);
    padding: 16px 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    /* overflow: hidden; */

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
      border-radius: var(--radius-m); /* 对齐看板圆角 */
    }

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      position: relative;
      z-index: 1;
    }

    .key_slot {
      width: 130px;
      height: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 10px 14px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 12px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 0;
      }

      .key_display {
        font-size: 22px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 80px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }

        &.empty {
          .key_display {
            font-size: 16px;
            color: var(--color-text-hint);
            font-weight: 600;
          }
        }

        .edit_hint {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 14px;
          height: 14px;
          color: var(--color-text-hint);
          opacity: 0.4;
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;

      .flow_icon {
        width: 20px;
        height: 20px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }
      .flow_label {
        font-size: 11px;
        font-weight: 900;
        color: var(--brand-color-pure);
        letter-spacing: 1px;
        opacity: 0.7;
      }
    }
  }

  /* 模式选择 - 现代化紧凑网格 */
  .modes_section {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .section_header {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 4px;

      .title {
        font-size: 11px;
        font-weight: 700;
        color: var(--color-text-hint);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .divider {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0) 100%);
      }
    }

    .modes_group {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .mode_card_item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 10px 4px;
      background: #ffffff;
      border-radius: var(--radius-m);
      border: 1px solid rgba(0, 0, 0, 0.03);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
      cursor: pointer;
      transition: var(--transition-base);
      min-height: 60px;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
        border-color: rgba(0, 0, 0, 0.08);
      }

      &.active {
        background: #fff;
        border-color: var(--brand-color-pure);
        box-shadow: 0 10px 30px rgba(255, 124, 0, 0.12);

        .mode_info_text .mode_name {
          color: var(--brand-color-pure);
        }
      }

      .mode_main_content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        width: 100%;
      }

      .mode_icon_box {
        width: 24px;
        height: 24px;
        border-radius: var(--radius-s);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        &.last_input {
          background: rgba(0, 150, 255, 0.1);
          .dot_indicator {
            background: #0096ff;
            box-shadow: 0 0 8px rgba(0, 150, 255, 0.4);
          }
        }
        &.a_first {
          background: rgba(255, 124, 0, 0.1);
          .dot_indicator {
            background: #ff7c00;
            box-shadow: 0 0 8px rgba(255, 124, 0, 0.4);
          }
        }
        &.b_first {
          background: rgba(147, 51, 234, 0.1);
          .dot_indicator {
            background: #9333ea;
            box-shadow: 0 0 8px rgba(147, 51, 234, 0.4);
          }
        }
        &.neutral {
          background: rgba(106, 114, 130, 0.1);
          .dot_indicator {
            background: #6a7282;
            box-shadow: 0 0 8px rgba(106, 114, 130, 0.4);
          }
        }

        .dot_indicator {
          width: 4px;
          height: 4px;
          border-radius: 50%;
        }
      }

      .mode_info_text {
        overflow: hidden;
        width: 100%;
        text-align: center;

        .mode_name {
          font-size: 11px;
          font-weight: 800;
          color: var(--color-dark);
          transition: color 0.3s;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          padding: 0 4px;
        }
      }
    }
  }

  .kme_settings_section {
    width: 100%;

    .mgx_setting_item_card {
      padding: 14px 20px;

      .setting_header {
        margin-bottom: 10px;
      }

      .setting_slider_container {
        .slider_labels {
          margin-top: 6px;
        }
      }
    }
  }
}
</style>
